import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  GraduationCap,
  Users,
  Menu,
  X,
  ChevronDown,
  Moon,
  Sun,
  Bell,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySwitcher from './CurrencySwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { UserMenu } from '@/components/auth/UserMenu';
import { useUnreadNotificationCount } from '@/hooks/use-notification-api';

const Navbar = () => {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, isRTL } = useLanguage();
  const { isLoggedIn, isLoading, role } = useAuth();
  const { data: unreadData } = useUnreadNotificationCount();
  const unreadCount = isLoggedIn ? ((unreadData as any)?.count || 0) : 0;
  const notifPath = role === 'teacher' ? '/teacher/notifications' : role === 'admin' ? '/admin' : role === 'parent' ? '/family' : '/student/notifications';
  // Parents have no in-app notification centre yet — they are alerted by
  // email/WhatsApp — so the bell is hidden rather than pointed at a portal
  // their role cannot enter.
  const showNotifications = role !== 'parent';
  const { theme, isDark, toggleMode } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/teachers', icon: GraduationCap, label: t('nav.teachers') },
    { to: '/courses', icon: BookOpen, label: t('nav.courses') },
    { to: '/about', icon: Users, label: t('nav.about') },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-background/80 backdrop-blur-xl shadow-soft border-b border-border/50'
          : 'bg-background/50 backdrop-blur-sm'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center group-hover:shadow-glow-emerald transition-shadow duration-300">
            <span lang="ar" className="text-primary-foreground font-arabic text-2xl font-bold leading-none">هـ</span>
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm text-foreground leading-tight">
              {isRTL ? <span lang="ar">هُدًى لِّلْمُتَّقِينَ</span> : 'Hudan'}
            </span>
            {!isRTL && (
              <span className="text-[10px] text-muted-foreground leading-tight tracking-wide">
                LIL MUTTAQEEN
              </span>
            )}
          </div>
        </Link>

        {/* Desktop Nav */}
        {!isMobile && (
          <div className="flex items-center gap-1">
            <nav aria-label="Main navigation" className="flex items-center gap-1 me-4">
              {navLinks.map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(to)
                      ? 'text-primary bg-primary/8'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <CurrencySwitcher />
              <LanguageSwitcher />
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMode}
                className="h-9 w-9 rounded-lg"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>

              {isLoggedIn ? (
                <>
                  {showNotifications && <Link
                    to={notifPath}
                    className="relative p-2 rounded-lg hover:bg-accent transition-colors"
                    aria-label={
                      isRTL
                        ? `الإشعارات${unreadCount > 0 ? ` (${unreadCount} غير مقروء)` : ''}`
                        : `${unreadCount > 0 ? `${unreadCount} unread ` : ''}notifications`
                    }
                  >
                    <Bell className="h-4.5 w-4.5 text-muted-foreground" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>}
                  <UserMenu />
                </>
              ) : isLoading ? (
                <div className="w-9 h-9 rounded-lg bg-muted animate-pulse" />
              ) : (
                <>
                  <Button asChild variant="ghost" size="sm" className="rounded-lg">
                    <Link to="/login">{t('nav.login')}</Link>
                  </Button>
                  <Button asChild size="sm" className="rounded-lg bg-primary hover:bg-primary/90">
                    <Link to="/register">{t('nav.register')}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Mobile Nav */}
        {isMobile && (
          <div className="flex items-center gap-2">
            <CurrencySwitcher />
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMode}
              className="h-9 w-9 rounded-lg"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            {isLoggedIn ? (
              <>
                {showNotifications && <Link
                  to={notifPath}
                  className="relative p-2 rounded-lg hover:bg-accent transition-colors"
                  aria-label={
                    isRTL
                      ? `الإشعارات${unreadCount > 0 ? ` (${unreadCount} غير مقروء)` : ''}`
                      : `${unreadCount > 0 ? `${unreadCount} unread ` : ''}notifications`
                  }
                >
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -end-0.5 min-w-[16px] h-[16px] px-0.5 rounded-full bg-destructive text-destructive-foreground text-[9px] font-semibold flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>}
                <UserMenu />
              </>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="h-9 w-9 rounded-lg"
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && isMobile && !isLoggedIn && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden border-t border-border/50 bg-background/95 backdrop-blur-xl"
          >
            <nav aria-label="Mobile navigation" className="container py-4 flex flex-col gap-1">
              {navLinks.map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isActive(to)
                      ? 'text-primary bg-primary/8'
                      : 'text-foreground hover:bg-accent'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{label}</span>
                </Link>
              ))}
              <div className="border-t border-border/50 mt-2 pt-3 flex gap-2">
                <Button asChild variant="outline" className="flex-1 rounded-lg">
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>{t('nav.login')}</Link>
                </Button>
                <Button asChild className="flex-1 rounded-lg">
                  <Link to="/register" onClick={() => setIsMenuOpen(false)}>{t('nav.register')}</Link>
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
